import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { useApp } from "../context/AppContext";
import { Endpoints } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Dialog } from "../components/ui/dialog";
import { Input } from "../components/ui/input";

const EMPTY = {
  name: "", daily_hour_limit: 10, weekly_hour_limit: 48,
  min_consecutive_rest: 11, max_weekly_overtime_h: 5,
};

export default function ProfessionsPage() {
  const { professions, agents, requirements, refreshLists } = useApp();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const usage = (id) => ({
    agents: agents.filter((a) => a.profession_id === id).length,
    reqs: requirements.filter((r) => (r.target_professions || []).includes(id)).length,
  });

  const openCreate = () => { setEditId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => {
    setEditId(p.id);
    setForm({
      name: p.name, daily_hour_limit: p.daily_hour_limit, weekly_hour_limit: p.weekly_hour_limit,
      min_consecutive_rest: p.min_consecutive_rest, max_weekly_overtime_h: p.max_weekly_overtime_h,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    const body = {
      name: form.name.trim(),
      daily_hour_limit: Number(form.daily_hour_limit),
      weekly_hour_limit: Number(form.weekly_hour_limit),
      min_consecutive_rest: Number(form.min_consecutive_rest),
      max_weekly_overtime_h: Number(form.max_weekly_overtime_h),
    };
    try {
      if (editId) await Endpoints.updateProfession(editId, body);
      else await Endpoints.createProfession(body);
      await refreshLists();
      setOpen(false);
      toast.success(editId ? "Profession updated" : "Profession added", {
        description: "Regenerate the schedule to apply the new limits.",
      });
    } catch (e) {
      toast.error("Could not save profession", { description: e?.response?.data?.detail || e.message });
    }
  };

  const remove = async (p) => {
    try {
      await Endpoints.deleteProfession(p.id);
      await refreshLists();
      toast.success("Profession removed");
    } catch (e) {
      toast.error("Cannot delete profession", { description: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="animate-fadeup">
      <PageHeader title="Professions" subtitle="Roles that carry the labour-law limits. Every agent inherits their daily / weekly caps, minimum rest and contractual overtime allowance from their profession.">
        <Button variant="primary" size="sm" onClick={openCreate} data-testid="add-profession-btn">
          <Plus className="w-4 h-4" /> New profession
        </Button>
      </PageHeader>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-line text-[11px] uppercase tracking-wider text-muted">
              <Th>Profession</Th><Th>Daily cap</Th><Th>Weekly cap</Th><Th>Min rest</Th><Th>Max OT / wk</Th><Th>In use</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {professions.map((p) => {
              const u = usage(p.id);
              return (
                <tr key={p.id} className="border-b border-line last:border-0 hover:bg-white/5 transition-colors duration-100" data-testid={`profession-row-${p.id}`}>
                  <Td className="text-ink font-medium">{p.name}</Td>
                  <Td className="mono text-muted">{p.daily_hour_limit}h</Td>
                  <Td className="mono text-muted">{p.weekly_hour_limit}h</Td>
                  <Td className="mono text-muted">{p.min_consecutive_rest}h</Td>
                  <Td className="mono text-muted">+{p.max_weekly_overtime_h}h</Td>
                  <Td className="mono text-faint text-xs">{u.agents} agents · {u.reqs} requirements</Td>
                  <Td className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`edit-profession-${p.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="danger" size="icon" onClick={() => remove(p)} data-testid={`delete-profession-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} title={editId ? "Edit profession" : "New profession"} testid="profession-dialog"
        footer={<><Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={save} data-testid="save-profession-btn">{editId ? "Save changes" : "Add profession"}</Button></>}>
        <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profession-name-input" placeholder="e.g. Ramp Agent" />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Daily hour limit" type="number" min="0" step="0.25" value={form.daily_hour_limit} onChange={(e) => setForm({ ...form, daily_hour_limit: e.target.value })} data-testid="profession-daily-input" />
          <Input label="Weekly hour limit" type="number" min="0" step="0.25" value={form.weekly_hour_limit} onChange={(e) => setForm({ ...form, weekly_hour_limit: e.target.value })} data-testid="profession-weekly-input" />
          <Input label="Min consecutive rest (h)" type="number" min="0" step="0.25" value={form.min_consecutive_rest} onChange={(e) => setForm({ ...form, min_consecutive_rest: e.target.value })} data-testid="profession-rest-input" />
          <Input label="Max weekly overtime (h)" type="number" min="0" step="0.25" value={form.max_weekly_overtime_h} onChange={(e) => setForm({ ...form, max_weekly_overtime_h: e.target.value })} data-testid="profession-ot-input" />
        </div>
        <p className="text-[11px] text-muted -mt-1">Effective weekly ceiling = min(weekly hour limit, skeleton hours + max overtime).</p>
      </Dialog>
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 font-medium">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
